import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, UserPlus, Search } from 'lucide-react';
import AddCustomerModal from './AddCustomerModal';
import { customersAPI } from '@/services/api';
import { toast } from 'sonner';

interface SelectCustomerModalProps {
    open: boolean;
    onClose: () => void;
    onSelect: (customer: any) => void;
}

const SelectCustomerModal: React.FC<SelectCustomerModalProps> = ({ open, onClose, onSelect }) => {
    const [customers, setCustomers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState('');
    const [addOpen, setAddOpen] = useState(false);

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            const res = await customersAPI.list({ q });
            setCustomers(res.customers || []);
        } catch (err: any) {
            toast.error(err?.response?.data?.error || err?.message || 'خطا در دریافت مشتری‌ها');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (open) fetchCustomers(); }, [open]);

    const filtered = useMemo(() => {
        const text = q.trim().toLowerCase();
        return customers.filter(
            (c) =>
                !text ||
                [c.first_name, c.last_name, c.phone_number, c.email]
                    .filter(Boolean)
                    .some((t) => String(t).toLowerCase().includes(text))
        );
    }, [customers, q]);

    return (
        <>
            <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
                <DialogContent dir="rtl" className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>انتخاب مشتری</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                <Input
                                    placeholder="جستجو بر اساس نام، شماره، ایمیل..."
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    className="pl-9"
                                />
                            </div>
                            <Button variant="outline" onClick={fetchCustomers}>جستجو</Button>
                            <Button
                                variant="outline"
                                className="border-green-300 text-green-700 hover:bg-green-50"
                                onClick={() => setAddOpen(true)}
                            >
                                <UserPlus className="ml-2 h-4 w-4" />
                                افزودن
                            </Button>
                        </div>

                        <div className="border rounded-lg max-h-[400px] overflow-auto divide-y">
                            {loading ? (
                                <div className="flex justify-center items-center py-6">
                                    <Loader2 className="h-6 w-6 animate-spin text-green-600" />
                                </div>
                            ) : filtered.length === 0 ? (
                                <div className="text-center py-6 text-gray-500">مشتری یافت نشد</div>
                            ) : (
                                filtered.map((c) => (
                                    <button
                                        type="button"
                                        key={c.id}
                                        className="w-full text-right p-3 hover:bg-green-50 cursor-pointer flex justify-between items-center"
                                        onClick={() => { onSelect(c); onClose(); }}
                                    >
                                        <div className="space-y-1">
                                            <div className="font-semibold text-green-900">
                                                {c.first_name} {c.last_name}
                                            </div>
                                            <div className="text-sm text-gray-600">{c.phone_number}</div>
                                            <div className="text-xs text-gray-500">{c.email}</div>
                                        </div>
                                        <div className="text-sm text-gray-700">{c.city_name || '—'}</div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* مودال افزودن مشتری */}
            <AddCustomerModal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onAdded={() => { setAddOpen(false); fetchCustomers(); }}
            />
        </>
    );
};

export default SelectCustomerModal;
